import { createApi } from '@reduxjs/toolkit/query/react';
import type { CustomerCreditBalance } from '@shared/models/creditBalance';
import { baseCommunicationQueryWithMsalAuth } from './baseApi';
import { CUSTOMER_CREDIT_BALANCE_ROUTE, isInvariantDecimalString } from './creditBalanceConstants';

type CreditBalancePayload = Record<string, unknown>;
type CanonicalCreditBalancePayload = CreditBalancePayload & CustomerCreditBalance;

const requiredCanonicalKeys = [
  'availableCredits',
  'maxCredits',
  'usedCredits',
  'periodStartUtc',
  'periodEndUtc',
] as const;

const legacyAliases = {
  availableCredits: 'available_credits',
  maxCredits: 'max_credits',
  usedCredits: 'used_credits',
  periodStartUtc: 'period_start_utc',
  periodEndUtc: 'period_end_utc',
} as const;

const isValidUtcTimestamp = (value: unknown): value is string =>
  typeof value === 'string' && !Number.isNaN(Date.parse(value));

const isCanonicalCreditBalance = (
  payload: CreditBalancePayload,
): payload is CanonicalCreditBalancePayload =>
  isInvariantDecimalString(payload.availableCredits) &&
  isInvariantDecimalString(payload.maxCredits) &&
  isInvariantDecimalString(payload.usedCredits) &&
  isValidUtcTimestamp(payload.periodStartUtc) &&
  isValidUtcTimestamp(payload.periodEndUtc);

const toCustomerCreditBalance = (
  payload: CanonicalCreditBalancePayload,
): CustomerCreditBalance => ({
  availableCredits: payload.availableCredits,
  maxCredits: payload.maxCredits,
  usedCredits: payload.usedCredits,
  periodStartUtc: payload.periodStartUtc,
  periodEndUtc: payload.periodEndUtc,
});

/**
 * Isolated read-only compatibility mapping for an integer-only mixed-version response.
 * New responses must be canonical camel-case and never pass through this adapter.
 */
export const normalizeCustomerCreditBalance = (
  payload: CreditBalancePayload,
  legacyAliasesEnabled = false,
): CustomerCreditBalance => {
  if (isCanonicalCreditBalance(payload)) {
    return toCustomerCreditBalance(payload);
  }

  if (!legacyAliasesEnabled) {
    throw new Error('Invalid customer credit balance response.');
  }

  const normalized = Object.fromEntries(
    requiredCanonicalKeys.map((key) => [key, payload[legacyAliases[key]]]),
  ) as CreditBalancePayload;

  if (
    !isCanonicalCreditBalance(normalized) ||
    ![normalized.availableCredits, normalized.maxCredits, normalized.usedCredits].every((value) => /^\d+$/.test(value))
  ) {
    throw new Error('Invalid legacy customer credit balance response.');
  }

  return toCustomerCreditBalance(normalized);
};

export const customerCreditBalanceApi = createApi({
  reducerPath: 'customerCreditBalanceApi',
  baseQuery: baseCommunicationQueryWithMsalAuth,
  endpoints: (builder) => ({
    getCreditBalance: builder.query<CustomerCreditBalance, void>({
      query: () => ({ url: CUSTOMER_CREDIT_BALANCE_ROUTE, method: 'GET' }),
      transformResponse: (response: CreditBalancePayload) => normalizeCustomerCreditBalance(response),
    }),
  }),
});

export const { useGetCreditBalanceQuery } = customerCreditBalanceApi;
